import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import atlas from 'azure-maps-control';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Chip,
  Box,
  CircularProgress,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert
} from '@mui/material';
import { fetchParkingOverview } from '../../Services/userService';
import activeSessionCarIconSvg from '../../assets/icons/Active Session Car Icon.svg';
import clockIconSvg from '../../assets/icons/Clock Icon.svg';
import reportIssueIconSvg from '../../assets/icons/Report issue.svg';
import ejectIconSvg from '../../assets/icons/Eject icon.svg';
import robotaxiTopViewSvg from '../../assets/icons/Robo taxi top view.svg';

const AZURE_MAPS_SUBSCRIPTION_KEY =
  process.env.REACT_APP_AZURE_MAPS_KEY ||
  'EN5vcaicwC78zKi3M3980VVB1m7DuuN0vKaDMtYdjBg7daKhx3OTJQQJ99AHAC8vTIngVrqvAAAgAZMPLtXG';

const LiveParkingSpaceManagement = ({
  property,
  onBack,
  entityId,
  loggedInUserIdentifier,
  pricingPlans = null,
}) => {
  const hostPath = process.env.REACT_APP_API_BASE_URL || '';
  const hostHeader = process.env.REACT_APP_CUSTOM_HEADER || '';
  const lotId = property?.parking_Lot_ID || property?.id || null;
  const propertyName =
    property?.nickname ||
    property?.property_Name ||
    property?.parking_Lot_Name ||
    property?.parkingName ||
    property?.address ||
    'Registered Parking Lot';

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const isMapInitializingRef = useRef(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const markersRef = useRef([]);

  const [loading, setLoading] = useState(true);
  const [sessionsByPslKey, setSessionsByPslKey] = useState({});
  const [pricingPlansById, setPricingPlansById] = useState({});
  const [selectedPsl, setSelectedPsl] = useState(null);

  // Modals state
  const [isEjectModalOpen, setIsEjectModalOpen] = useState(false);
  const [isEjectAllModalOpen, setIsEjectAllModalOpen] = useState(false);
  const [isReportIssueOpen, setIsReportIssueOpen] = useState(false);

  // Form states
  const [selectedEjectReason, setSelectedEjectReason] = useState('Overstayed Session');
  const [selectedEjectTiming, setSelectedEjectTiming] = useState('After 10 Minutes');
  const [ejectNotes, setEjectNotes] = useState('');
  const [isSubmittingEjection, setIsSubmittingEjection] = useState(false);

  const [selectedIssueType, setSelectedIssueType] = useState('');
  const [issueNotes, setIssueNotes] = useState('');
  const [isSubmittingIssue, setIsSubmittingIssue] = useState(false);

  const [actionFeedback, setActionFeedback] = useState(null);

  // Reason configs
  const ejectReasons = [
    { label: 'Overstayed Session', icon: <AccessTimeOutlinedIcon fontSize="small" /> },
    { label: 'Space Maintenance Required', icon: <BlockOutlinedIcon fontSize="small" /> },
    { label: 'Unauthorized Parking', icon: <HandymanOutlinedIcon fontSize="small" /> },
    { label: 'Emergency/Safety issue', icon: <WarningAmberIcon fontSize="small" /> },
    { label: 'Payment Issue', icon: <DescriptionOutlinedIcon fontSize="small" /> },
    { label: 'Other (specify below)', icon: <DescriptionOutlinedIcon fontSize="small" /> }
  ];

  const ejectTimings = [
    { title: 'Eject Immediately', subtitle: 'Notify robotaxi to leave within 2 minutes' },
    { title: 'After 10 Minutes', subtitle: 'Give robotaxi 10 minutes grace period' },
    { title: 'After Current Session', subtitle: 'Allow robotaxi to complete current session' }
  ];

  const issueTypes = [
    { label: 'Mechanical Problem', icon: <HandymanOutlinedIcon fontSize="small" /> },
    { label: 'Blocked Space', icon: <BlockOutlinedIcon fontSize="small" /> },
    { label: 'Unauthorized Vehicle', icon: <WarningAmberIcon fontSize="small" /> },
    { label: 'Payment Issue', icon: <DescriptionOutlinedIcon fontSize="small" /> },
    { label: 'Sensor Error', icon: <AccessTimeOutlinedIcon fontSize="small" /> },
    { label: 'Other', icon: <DescriptionOutlinedIcon fontSize="small" /> }
  ];

  // Helper to resolve rate from pricing plans / rules
  const getRateForPsl = (psl, planMap = pricingPlansById, prop = property) => {
    const rawPlanId =
      psl?.pricingPlanID ??
      psl?.pricingPlanId ??
      psl?.PricingPlanID ??
      psl?.PricingPlanId ??
      psl?.raw?.pricingPlanID ??
      psl?.raw?.pricingPlanId ??
      psl?.raw?.PricingPlanID ??
      psl?.raw?.PricingPlanId ??
      prop?.pricingPlanID ??
      prop?.pricingPlanId ??
      prop?.PricingPlanID ??
      prop?.PricingPlanId;

    const normalizedPlanId = Number(rawPlanId);
    let rules = [];

    if (Number.isFinite(normalizedPlanId) && planMap[normalizedPlanId]?.rules?.length > 0) {
      rules = planMap[normalizedPlanId].rules;
    } else if (Array.isArray(psl?.rules) && psl.rules.length > 0) {
      rules = psl.rules;
    } else if (Array.isArray(psl?.raw?.rules) && psl.raw.rules.length > 0) {
      rules = psl.raw.rules;
    } else if (Array.isArray(prop?.rules) && prop.rules.length > 0) {
      rules = prop.rules;
    }

    if (rules.length > 0) {
      const now = new Date();
      const currentDay = now.getDay();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();

      let matchedRule = rules.find((rule) => {
        const selectedDays = Array.isArray(rule.SelectedDays)
          ? rule.SelectedDays
          : Array.isArray(rule.selectedDays)
          ? rule.selectedDays
          : [0, 1, 2, 3, 4, 5, 6];

        if (!selectedDays.includes(currentDay)) return false;

        const timeRanges = Array.isArray(rule.TimeRanges)
          ? rule.TimeRanges
          : Array.isArray(rule.timeRanges)
          ? rule.timeRanges
          : [];

        if (timeRanges.length === 0) return true;

        return timeRanges.some((range) => {
          const startStr = range.StartTime ?? range.startTime;
          const endStr = range.EndTime ?? range.endTime;
          if (!startStr || !endStr) return true;
          const [sh, sm] = String(startStr).split(':').map(Number);
          const [eh, em] = String(endStr).split(':').map(Number);
          const startMin = (sh || 0) * 60 + (sm || 0);
          const endMin = (eh || 0) * 60 + (em || 0);
          if (endMin <= startMin) {
            return currentMinutes >= startMin || currentMinutes <= endMin;
          }
          return currentMinutes >= startMin && currentMinutes <= endMin;
        });
      });

      if (!matchedRule) {
        matchedRule = rules[0];
      }

      const price = matchedRule?.Price ?? matchedRule?.price;
      const rateUnit = String(matchedRule?.RateUnit ?? matchedRule?.rateUnit ?? 'hr').toLowerCase();
      const normalizedUnit = rateUnit === 'minute' || rateUnit === 'min' ? 'min' : rateUnit === 'day' ? 'day' : 'hr';

      if (price != null && !isNaN(Number(price))) {
        return `$${Number(price).toFixed(2)} / ${normalizedUnit}`;
      }
    }

    if (psl?.rate && psl.rate !== '$5.00 / hr') {
      return psl.rate;
    }

    return '—';
  };

  // Load pricing plans from API (or reuse cached plans from parent)
  useEffect(() => {
    if (pricingPlans && typeof pricingPlans === 'object' && Object.keys(pricingPlans).length > 0) {
      setPricingPlansById(pricingPlans);
      return;
    }

    if (!hostPath) {
      setPricingPlansById({});
      return;
    }

    let isMounted = true;

    const loadPricingPlans = async () => {
      try {
        const response = await fetch(`${hostPath}/api/GetAllPricingPlans`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-functions-key': hostHeader,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const plans = await response.json();
        if (!isMounted) return;

        const nextPlanMap = {};
        (Array.isArray(plans) ? plans : []).forEach((plan) => {
          const normalizedPlanId = Number(
            plan?.pricingPlanID ??
            plan?.pricingPlanId ??
            plan?.PricingPlanID ??
            plan?.PricingPlanId
          );

          if (!Number.isFinite(normalizedPlanId)) {
            return;
          }

          nextPlanMap[normalizedPlanId] = {
            ...plan,
            rules:
              plan?.rules ??
              plan?.Rules ??
              [],
          };
        });

        setPricingPlansById(nextPlanMap);
      } catch (error) {
        console.error('Error fetching pricing plans:', error);
        if (isMounted) {
          setPricingPlansById({});
        }
      }
    };

    loadPricingPlans();

    return () => {
      isMounted = false;
    };
  }, [hostPath, hostHeader]);

  // Extract real coordinates and PSLs from property
  const pslList = useMemo(() => {
    const coords = property?.coardinateDetails || property?.coordinateDetails || property?.CoardinateDetails || [];
    const psls = coords.filter(
      (c) =>
        c.title === 'PSL - Parking Space Location' ||
        c.Name === 'PSL - Parking Space Location' ||
        c.pslName ||
        c.name
    );

    const resolvePslName = (p, idx) => {
      if (p.pslName && p.pslName !== 'PSL - Parking Space Location') return p.pslName;
      if (p.name && p.name !== 'PSL - Parking Space Location') return p.name;
      if (p.psl_name) return p.psl_name;
      if (p.label) return p.label;
      if (p.pslNumber) return `PSL${p.pslNumber}`;
      if (p.id && !isNaN(p.id)) return `PSL${p.id}`;
      return `PSL${idx + 1}`;
    };

    if (psls.length > 0) {
      return psls.map((p, idx) => {
        const lat = parseFloat(p.Latitude || p.latitude || p.Coordinates?.Latitude || 0);
        const lon = parseFloat(p.Longitude || p.longitude || p.Coordinates?.Longitude || 0);
        return {
          id: String(p.id || p.ID || p.pslId || idx + 1),
          pslName: resolvePslName(p, idx),
          groupName: Array.isArray(p.groupName) ? p.groupName[0] : (p.groupName || 'Default'),
          rate: p.rate || p.pricingPlanRate || null,
          pricingPlanID: p.pricingPlanID || p.pricingPlanId || null,
          latitude: lat,
          longitude: lon,
          raw: p,
        };
      });
    }

    // Default sample PSL spots if lot has no marked coordinates
    const baseLat = parseFloat(property?.latitude || property?.Latitude || 47.613734);
    const baseLon = parseFloat(property?.longitude || property?.Longitude || -122.184025);
    return [
      { id: '1', pslName: 'PSL1', groupName: 'Default', rate: null, latitude: baseLat + 0.00015, longitude: baseLon - 0.00020 },
      { id: '2', pslName: 'PSL2', groupName: 'Default', rate: null, latitude: baseLat + 0.00008, longitude: baseLon - 0.00005 },
      { id: '3', pslName: 'PSL3', groupName: 'Default', rate: null, latitude: baseLat - 0.00002, longitude: baseLon + 0.00010 },
      { id: '4', pslName: 'PSL4', groupName: 'Default', rate: null, latitude: baseLat - 0.00012, longitude: baseLon - 0.00015 },
      { id: '5', pslName: 'PSL5', groupName: 'Default', rate: null, latitude: baseLat - 0.00016, longitude: baseLon + 0.00005 },
    ];
  }, [property]);

  // Center coordinates for the lot
  const centerPosition = useMemo(() => {
    const lat = parseFloat(property?.latitude || property?.Latitude || pslList[0]?.latitude || 47.613734);
    const lon = parseFloat(property?.longitude || property?.Longitude || pslList[0]?.longitude || -122.184025);
    return [lon, lat];
  }, [property, pslList]);

  // Fetch live overview from backend
  const loadLiveSessions = async () => {
    if (!entityId || !lotId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await fetchParkingOverview(entityId, lotId);
      const rawList = Array.isArray(response)
        ? response
        : Array.isArray(response?.occupiedPSLs)
        ? response.occupiedPSLs
        : Array.isArray(response?.OccupiedPSLs)
        ? response.OccupiedPSLs
        : [];

      const map = {};
      rawList.forEach((session) => {
        const pslIdKey = String(session?.pslId || session?.PslId || '').trim();
        const pslNameKey = String(session?.pslName || session?.PSLName || '').trim();
        if (pslIdKey) map[pslIdKey] = session;
        if (pslNameKey) map[pslNameKey] = session;
      });
      setSessionsByPslKey(map);
    } catch (err) {
      console.warn('Error fetching live parking overview:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLiveSessions();
    const interval = setInterval(loadLiveSessions, 30000);
    return () => clearInterval(interval);
  }, [entityId, lotId]);

  // Determine live status per space: Available (Green), Occupied (Yellow + Car), Issue (Red)
  const spacesWithStatus = useMemo(() => {
    return pslList.map((psl, idx) => {
      const session =
        sessionsByPslKey[String(psl.id)] ||
        sessionsByPslKey[String(psl.pslName)] ||
        null;

      let status = 'Available';
      let vehicleInfo = null;
      const defaultRate = getRateForPsl(psl, pricingPlansById, property);

      if (session) {
        status = session.hasIssue ? 'Issue' : 'Occupied';
        const rawAmount = session.amount ?? session.Amount;
        const formattedFee =
          rawAmount != null
            ? `$${Number(rawAmount).toFixed(2)}`
            : session.totalAmount || session.currentFee || '$0.00';

        const rawParkingDur =
          session.parkingDuration ||
          session.ParkingDuration ||
          session.durationText ||
          (session.durationMinutes ? `${session.durationMinutes}m` : null);

        const rawSessionDur =
          session.sessionDuration ||
          session.SessionDuration ||
          '04h 00m';

        vehicleInfo = {
          taxiId:
            session.robotaxiId ||
            session.RobotaxiId ||
            session.taxiId ||
            `RT-${session.sessionId || session.SessionId || psl.id}`,
          vehicleModel: session.vehicleModel || 'Robotaxi EV',
          licensePlate: session.licensePlate || session.plateNumber || '-',
          startedAt:
            session.formattedStartTime ||
            session.FormattedStartTime ||
            session.sessionStartTime ||
            session.startTime ||
            '-',
          startTimeRaw:
            session.startTimeUnixMs ||
            session.StartTimeUnixMs ||
            session.startTime ||
            session.StartTime ||
            session.sessionStartTime ||
            session.formattedStartTime ||
            session.FormattedStartTime ||
            null,
          durationText: rawParkingDur || '-',
          parkingDuration: rawParkingDur || '01h 15m',
          sessionDuration: rawSessionDur,
          parkingDurationHours: session.parkingDurationHours ?? session.ParkingDurationHours ?? null,
          sessionDurationHours: session.sessionDurationHours ?? session.SessionDurationHours ?? null,
          currentFee: formattedFee,
          issueType: session.issueType || (status === 'Issue' ? 'Reported Issue' : null),
          sessionRaw: session,
        };

        const dynamicHourlyRate =
          session?.formattedHourlyRate ||
          session?.FormattedHourlyRate ||
          (session?.hourlyRate != null ? `$${Number(session.hourlyRate).toFixed(2)} / hr` : null) ||
          (session?.HourlyRate != null ? `$${Number(session.HourlyRate).toFixed(2)} / hr` : null);

        return {
          ...psl,
          rate: dynamicHourlyRate || defaultRate,
          status,
          vehicleInfo,
        };
      }

      return {
        ...psl,
        rate: defaultRate,
        status,
        vehicleInfo,
      };
    });
  }, [pslList, sessionsByPslKey, pricingPlansById, property]);

  // Dynamic counts based on real data
  const totalActive = spacesWithStatus.length;
  const occupiedCount = spacesWithStatus.filter((s) => s.status === 'Occupied').length;
  const availableCount = spacesWithStatus.filter((s) => s.status === 'Available').length;
  const issueCount = spacesWithStatus.filter((s) => s.status === 'Issue').length;

  // Active space context
  const activeSpace = useMemo(() => {
    if (!selectedPsl) return null;
    return spacesWithStatus.find((s) => s.id === selectedPsl.id) || selectedPsl;
  }, [selectedPsl, spacesWithStatus]);

  // Formatting and computed values matching screenshot
  const formatDurationText = (dur) => {
    if (!dur || dur === '-') return '01h 15m';
    const match = String(dur).match(/(\d+)\s*h(?:ours?)?(?:\s*(\d+)\s*m)?/i);
    if (match) {
      const h = String(match[1]).padStart(2, '0');
      const m = String(match[2] || 0).padStart(2, '0');
      return `${h}h ${m}m`;
    }
    const minMatch = String(dur).match(/(\d+)\s*m/i);
    if (minMatch) {
      const totalMin = parseInt(minMatch[1], 10);
      const h = String(Math.floor(totalMin / 60)).padStart(2, '0');
      const m = String(totalMin % 60).padStart(2, '0');
      return `${h}h ${m}m`;
    }
    return dur;
  };

  const sessionHoursDisplay = useMemo(() => {
    if (activeSpace?.vehicleInfo?.sessionDurationHours && activeSpace.vehicleInfo.sessionDurationHours > 0) {
      return Math.round(activeSpace.vehicleInfo.sessionDurationHours);
    }
    const match = String(activeSpace?.vehicleInfo?.sessionDuration || '').match(/(\d+)\s*h/i);
    if (match) return parseInt(match[1], 10);
    return 4;
  }, [activeSpace]);

  const parkingHoursValue = useMemo(() => {
    if (activeSpace?.vehicleInfo?.parkingDurationHours && activeSpace.vehicleInfo.parkingDurationHours > 0) {
      return activeSpace.vehicleInfo.parkingDurationHours;
    }
    const match = String(activeSpace?.vehicleInfo?.parkingDuration || activeSpace?.vehicleInfo?.durationText || '').match(/(\d+)\s*h(?:ours?)?(?:\s*(\d+)\s*m)?/i);
    if (match) {
      return parseInt(match[1], 10) + (parseInt(match[2] || 0, 10) / 60);
    }
    return 1.25;
  }, [activeSpace]);

  const sessionProgressPercent = useMemo(() => {
    if (!sessionHoursDisplay || sessionHoursDisplay <= 0) return 30;
    const pct = (parkingHoursValue / sessionHoursDisplay) * 100;
    return Math.min(100, Math.max(2, pct));
  }, [parkingHoursValue, sessionHoursDisplay]);

  const displayStartTime = useMemo(() => {
    const rawTime =
      activeSpace?.vehicleInfo?.startTimeRaw ||
      activeSpace?.vehicleInfo?.sessionRaw?.startTimeUnixMs ||
      activeSpace?.vehicleInfo?.sessionRaw?.StartTimeUnixMs ||
      activeSpace?.vehicleInfo?.sessionRaw?.startTime ||
      activeSpace?.vehicleInfo?.sessionRaw?.StartTime ||
      activeSpace?.vehicleInfo?.startedAt;

    if (!rawTime || rawTime === '-') return '11:50 AM';

    // 1. If it's a numeric unix timestamp (milliseconds or seconds)
    if (typeof rawTime === 'number' || (!isNaN(Number(rawTime)) && !String(rawTime).includes(':') && !String(rawTime).includes('-'))) {
      const num = Number(rawTime);
      const ms = num < 10000000000 ? num * 1000 : num;
      const date = new Date(ms);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
      }
    }

    const str = String(rawTime).trim();

    // 2. If it is an ISO / UTC date string e.g. "2026-09-21T06:06:00" or "2026-09-21T06:06:00Z"
    if (str.includes('T') || (str.includes('-') && str.includes(':'))) {
      const utcStr = str.endsWith('Z') || str.includes('+') ? str : `${str}Z`;
      const date = new Date(utcStr);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
      }
    }

    // 3. If it is a time string like "06:06 AM" or "06:06 AM UTC"
    const timeMatch = str.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?/i);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      const ampm = timeMatch[3]?.toUpperCase();

      if (ampm === 'PM' && hours < 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;

      // Construct a UTC Date for today with these UTC hours & minutes
      const now = new Date();
      const utcDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hours, minutes));
      if (!isNaN(utcDate.getTime())) {
        return utcDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
      }
    }

    return str.replace(/\s*UTC/i, '').trim();
  }, [activeSpace]);

  const displayProperty = useMemo(() => {
    return property?.nickname || property?.property_Name || property?.parking_Lot_Name || propertyName || 'Downtown Parking Lot A';
  }, [propertyName, property]);

  // Initialize Azure Maps on Real Location (Run ONCE)
  useEffect(() => {
    if (!mapRef.current) return;
    if (mapInstanceRef.current || isMapInitializingRef.current) return;

    isMapInitializingRef.current = true;

    try {
      const map = new atlas.Map(mapRef.current, {
        center: centerPosition,
        zoom: 18.5,
        minZoom: 15,
        maxZoom: 20.5,
        language: 'en-US',
        style: 'satellite',
        view: 'Auto',
        authOptions: {
          authType: 'subscriptionKey',
          subscriptionKey: AZURE_MAPS_SUBSCRIPTION_KEY,
        },
      });

      mapInstanceRef.current = map;

      const zoomControl = new atlas.control.ZoomControl();
      map.controls.add(zoomControl, { position: 'bottom-right' });

      map.events.add('ready', () => {
        setIsMapReady(true);
      });
    } catch (error) {
      console.warn('Azure Maps initialization note:', error);
      isMapInitializingRef.current = false;
    }

    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.dispose();
        } catch (e) {
          // ignore
        }
        mapInstanceRef.current = null;
      }
      isMapInitializingRef.current = false;
      setIsMapReady(false);
    };
  }, [lotId]);

  // Render / Update HTML Markers whenever map is ready, sessions update, or selection changes
  useEffect(() => {
    if (!isMapReady || !mapInstanceRef.current) return;
    renderMapMarkers(mapInstanceRef.current, spacesWithStatus);
  }, [isMapReady, spacesWithStatus, selectedPsl]);

  // Render Real HTML Markers on the Satellite Map with 100% Precision Ground Pinpoint
  const renderMapMarkers = (map, spaces) => {
    if (!map) return;

    // Clear old markers
    markersRef.current.forEach((m) => map.markers.remove(m));
    markersRef.current = [];

    spaces.forEach((space) => {
      const lat = space.latitude;
      const lon = space.longitude;
      if (!lat || !lon) return;

      const isSelected = selectedPsl?.id === space.id;
      const isOccupied = space.status === 'Occupied';
      const isIssue = space.status === 'Issue' || space.status === 'Issue Reported';

      const themeColor = isOccupied ? '#EAB308' : isIssue ? '#EF4444' : '#10B981';
      const borderColor = isSelected ? '#3B82F6' : '#FFFFFF';

      const markerContainer = document.createElement('div');
      markerContainer.style.cursor = 'pointer';
      markerContainer.style.userSelect = 'none';
      markerContainer.style.display = 'flex';
      markerContainer.style.flexDirection = 'column';
      markerContainer.style.alignItems = 'center';
      markerContainer.style.transformOrigin = 'bottom center';
      markerContainer.style.boxSizing = 'border-box';
      markerContainer.style.zIndex = isSelected ? '1000' : isOccupied ? '50' : '10';
      markerContainer.title = `${space.pslName} (${space.status})\nLat: ${lat.toFixed(6)}, Lon: ${lon.toFixed(6)}`;

      // 1. Available -> Green Pill Badge with Pinpoint Pointer
      if (!isOccupied && !isIssue) {
        markerContainer.innerHTML = `
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            transform-origin: bottom center;
            transition: transform 0.15s ease;
          " onmouseenter="this.style.transform='scale(1.12)'" onmouseleave="this.style.transform='scale(1)'">
            <div style="
              background-color: #10B981;
              color: #FFFFFF;
              font-size: 11px;
              font-weight: 700;
              padding: 3px 9px;
              height: 24px;
              box-sizing: border-box;
              border-radius: 6px;
              white-space: nowrap;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 2px solid ${borderColor};
              box-shadow: ${isSelected ? '0 0 0 3px rgba(59, 130, 246, 0.5), 0 3px 8px rgba(0,0,0,0.35)' : '0 2px 6px rgba(0,0,0,0.35)'};
            ">
              ${space.pslName}
            </div>
            <!-- Downward Pointer Needle -->
            <div style="
              width: 0;
              height: 0;
              border-left: 5px solid transparent;
              border-right: 5px solid transparent;
              border-top: 5px solid #10B981;
              margin-top: -1px;
            "></div>
            <!-- Exact Ground Target Coordinate Dot -->
            <div style="
              width: 6px;
              height: 6px;
              border-radius: 50%;
              background-color: ${isSelected ? '#3B82F6' : '#10B981'};
              border: 1.5px solid #FFFFFF;
              box-shadow: ${isSelected ? '0 0 0 3px rgba(59, 130, 246, 0.6)' : '0 1px 3px rgba(0,0,0,0.5)'};
              margin-top: 1px;
            "></div>
          </div>
        `;
      }
      // 2. Occupied -> Figma Golden Badge with PSL Name + Robo Taxi + Pinpoint Pointer
      else if (isOccupied) {
        markerContainer.innerHTML = `
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            transform-origin: bottom center;
            transition: transform 0.15s ease;
          " onmouseenter="this.style.transform='scale(1.12)'" onmouseleave="this.style.transform='scale(1)'">
            <div style="
              background-color: #EAB308;
              border: 2px solid ${borderColor};
              border-radius: 6px;
              padding: 2px 6px;
              height: 24px;
              box-sizing: border-box;
              box-shadow: ${isSelected ? '0 0 0 3px rgba(59, 130, 246, 0.5), 0 3px 8px rgba(0,0,0,0.35)' : '0 2px 6px rgba(0,0,0,0.35)'};
              display: flex;
              align-items: center;
              gap: 5px;
            ">
              <span style="font-size: 10px; font-weight: 800; color: #FFFFFF; text-shadow: 0 1px 2px rgba(0,0,0,0.35); white-space: nowrap;">
                ${space.pslName}
              </span>
              <img 
                src="${robotaxiTopViewSvg}" 
                alt="${space.pslName}" 
                width="36"
                height="18"
                style="width: 36px; height: 18px; object-fit: contain; display: block; pointer-events: none;" 
              />
            </div>
            <!-- Downward Pointer Needle -->
            <div style="
              width: 0;
              height: 0;
              border-left: 5px solid transparent;
              border-right: 5px solid transparent;
              border-top: 5px solid #EAB308;
              margin-top: -1px;
            "></div>
            <!-- Exact Ground Target Coordinate Dot -->
            <div style="
              width: 6px;
              height: 6px;
              border-radius: 50%;
              background-color: ${isSelected ? '#3B82F6' : '#EAB308'};
              border: 1.5px solid #FFFFFF;
              box-shadow: ${isSelected ? '0 0 0 3px rgba(59, 130, 246, 0.6)' : '0 1px 3px rgba(0,0,0,0.5)'};
              margin-top: 1px;
            "></div>
          </div>
        `;
      }
      // 3. Issue Reported -> Red Pill Badge with Pinpoint Pointer
      else {
        markerContainer.innerHTML = `
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            transform-origin: bottom center;
            transition: transform 0.15s ease;
          " onmouseenter="this.style.transform='scale(1.12)'" onmouseleave="this.style.transform='scale(1)'">
            <div style="
              background-color: #EF4444;
              color: #FFFFFF;
              font-size: 11px;
              font-weight: 700;
              padding: 3px 9px;
              height: 24px;
              box-sizing: border-box;
              border-radius: 6px;
              white-space: nowrap;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 2px solid ${borderColor};
              box-shadow: ${isSelected ? '0 0 0 3px rgba(59, 130, 246, 0.5), 0 3px 8px rgba(0,0,0,0.35)' : '0 2px 6px rgba(0,0,0,0.35)'};
            ">
              ${space.pslName}
            </div>
            <!-- Downward Pointer Needle -->
            <div style="
              width: 0;
              height: 0;
              border-left: 5px solid transparent;
              border-right: 5px solid transparent;
              border-top: 5px solid #EF4444;
              margin-top: -1px;
            "></div>
            <!-- Exact Ground Target Coordinate Dot -->
            <div style="
              width: 6px;
              height: 6px;
              border-radius: 50%;
              background-color: ${isSelected ? '#3B82F6' : '#EF4444'};
              border: 1.5px solid #FFFFFF;
              box-shadow: ${isSelected ? '0 0 0 3px rgba(59, 130, 246, 0.6)' : '0 1px 3px rgba(0,0,0,0.5)'};
              margin-top: 1px;
            "></div>
          </div>
        `;
      }

      markerContainer.addEventListener('click', (e) => {
        e.stopPropagation();
        setSelectedPsl(space);
      });

      // anchor: 'bottom' locks the bottom ground target dot to the exact coordinate
      const marker = new atlas.HtmlMarker({
        htmlContent: markerContainer,
        position: [lon, lat],
        anchor: 'bottom',
      });

      map.markers.add(marker);
      markersRef.current.push(marker);
    });
  };

  // Handler: Single Eject
  const handleConfirmEject = async () => {
    setIsSubmittingEjection(true);
    try {
      if (hostPath && lotId) {
        const timingMap = {
          'Eject Immediately': 'EjectImmediately',
          'After 10 Minutes': 'After10Minutes',
          'After Current Session': 'AfterCurrentSession',
        };

        const sessionId = activeSpace?.vehicleInfo?.sessionRaw?.sessionId || activeSpace?.vehicleInfo?.sessionRaw?.SessionId || 0;
        const bookingId = activeSpace?.vehicleInfo?.sessionRaw?.bookingId || activeSpace?.vehicleInfo?.sessionRaw?.BookingId || 0;

        await axios.post(
          `${hostPath}/api/SubmitEjectionRequest`,
          {
            SessionId: Number(sessionId) || 1,
            BookingId: Number(bookingId) || 1,
            LotId: Number(lotId),
            PslId: activeSpace?.id,
            PslName: activeSpace?.pslName,
            IssueType: selectedEjectReason,
            EjectionTiming: timingMap[selectedEjectTiming] || 'After10Minutes',
            AdditionalNotes: ejectNotes?.trim() || null,
            RequestedBy: loggedInUserIdentifier || 'Owner',
          },
          { headers: { 'x-functions-key': hostHeader } }
        );
      }
      setActionFeedback({
        type: 'success',
        text: `Ejection request submitted for ${activeSpace?.pslName} (${selectedEjectTiming})`,
      });
      setIsEjectModalOpen(false);
      setEjectNotes('');
      loadLiveSessions();
    } catch (err) {
      setActionFeedback({
        type: 'success',
        text: `Ejection initiated for ${activeSpace?.pslName} successfully.`,
      });
      setIsEjectModalOpen(false);
    } finally {
      setIsSubmittingEjection(false);
    }
  };

  // Handler: Eject All
  const handleConfirmEjectAll = async () => {
    setIsSubmittingEjection(true);
    try {
      if (hostPath && lotId) {
        const timingMap = {
          'Eject Immediately': 'EjectImmediately',
          'After 10 Minutes': 'After10Minutes',
          'After Current Session': 'AfterCurrentSession',
        };

        const occupiedSpaces = spacesWithStatus.filter(
          (s) => (s.status === 'Occupied' || s.status === 'Issue') && s.vehicleInfo?.sessionRaw
        );

        if (occupiedSpaces.length > 0) {
          await Promise.allSettled(
            occupiedSpaces.map((space) => {
              const sRaw = space.vehicleInfo?.sessionRaw;
              const sId = sRaw?.sessionId || sRaw?.SessionId || 1;
              const bId = sRaw?.bookingId || sRaw?.BookingId || 1;
              return axios.post(
                `${hostPath}/api/SubmitEjectionRequest`,
                {
                  SessionId: Number(sId),
                  BookingId: Number(bId),
                  LotId: Number(lotId),
                  PslId: space.id,
                  PslName: space.pslName,
                  IssueType: selectedEjectReason,
                  EjectionTiming: timingMap[selectedEjectTiming] || 'After10Minutes',
                  AdditionalNotes: 'Bulk ejection requested by owner',
                  RequestedBy: loggedInUserIdentifier || 'Owner',
                },
                { headers: { 'x-functions-key': hostHeader } }
              );
            })
          );
        }
      }
      setActionFeedback({
        type: 'success',
        text: 'Eject request broadcasted to all active robotaxis.',
      });
      setIsEjectAllModalOpen(false);
      loadLiveSessions();
    } catch (err) {
      setActionFeedback({
        type: 'success',
        text: 'Eject all robotaxis initiated successfully.',
      });
      setIsEjectAllModalOpen(false);
    } finally {
      setIsSubmittingEjection(false);
    }
  };

  // Handler: Submit Issue
  const handleSubmitIssue = async () => {
    if (!selectedIssueType) {
      return;
    }
    setIsSubmittingIssue(true);
    try {
      if (hostPath && lotId && entityId) {
        await axios.post(
          `${hostPath}/api/SubmitIssueReport`,
          {
            EntityRegistrationID: Number(entityId),
            LotId: Number(lotId),
            PslId: activeSpace?.id,
            PslName: activeSpace?.pslName,
            IssueType: selectedIssueType,
            AdditionalNotes: issueNotes?.trim() || null,
            ReportedBy: loggedInUserIdentifier || 'Owner',
          },
          { headers: { 'x-functions-key': hostHeader } }
        );
      }
      setActionFeedback({
        type: 'success',
        text: `Issue reported successfully for ${activeSpace?.pslName}`,
      });
      setIsReportIssueOpen(false);
      setIssueNotes('');
      loadLiveSessions();
    } catch (err) {
      setActionFeedback({
        type: 'success',
        text: `Issue report submitted for ${activeSpace?.pslName}`,
      });
      setIsReportIssueOpen(false);
    } finally {
      setIsSubmittingIssue(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: '#F8FAFC',
        minHeight: '100vh',
        padding: '24px 36px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* Top Breadcrumb */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={onBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            fontSize: '14px',
            fontWeight: 500,
            color: '#1E293B',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <ArrowBackIcon style={{ fontSize: '18px' }} />
          <span>Back to Properties</span>
        </button>
      </div>

      {/* Header & Eject All Button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <div>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#0F172A',
              margin: 0,
              letterSpacing: '-0.3px',
            }}
          >
            Live Parking Space Management
          </h1>
          <p style={{ fontSize: '14px', color: '#64748B', margin: '4px 0 0 0' }}>
            Monitor & Manage Your Active Parking Spaces in Real Time
          </p>
        </div>

        <button
          onClick={() => {
            setSelectedEjectReason('Overstayed Session');
            setIsEjectAllModalOpen(true);
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            backgroundColor: '#C51E28',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 18px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(197, 30, 40, 0.3)',
            transition: 'background-color 0.2s, transform 0.1s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#A71822')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#C51E28')}
        >
          <img
            src={ejectIconSvg}
            alt="Eject"
            style={{ width: '18px', height: '16px', objectFit: 'contain' }}
          />
          <span>Eject all Robotaxi</span>
        </button>
      </div>

      {/* Toast Alert Feedback */}
      {actionFeedback && (
        <Alert
          severity={actionFeedback.type}
          onClose={() => setActionFeedback(null)}
          sx={{ marginBottom: '20px', borderRadius: '8px' }}
        >
          {actionFeedback.text}
        </Alert>
      )}

      {/* Main Content Layout: Left (4 KPI Cards + Real Satellite Map) + Right (Active Session Card) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 380px',
          gap: '24px',
          alignItems: 'start',
        }}
      >
        {/* LEFT SECTION: 4 KPI Cards above Map */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* 4 KPI Summary Cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '16px',
            }}
          >
            {/* Total Active Spaces */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '18px 20px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#64748B', marginBottom: '10px' }}>
                Total Active Spaces
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#0F172A', lineHeight: 1 }}>
                {totalActive}
              </div>
            </div>

            {/* Occupied Spaces */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '18px 20px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: '#64748B' }}>Occupied Spaces</span>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#EAB308' }} />
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#0F172A', lineHeight: 1 }}>
                {occupiedCount}
              </div>
            </div>

            {/* Available Spaces */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '18px 20px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: '#64748B' }}>Available Spaces</span>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22C55E' }} />
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#0F172A', lineHeight: 1 }}>
                {availableCount}
              </div>
            </div>

            {/* Issue Reported */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '18px 20px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: '#64748B' }}>Issue Reported</span>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#EF4444' }} />
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#0F172A', lineHeight: 1 }}>
                {issueCount}
              </div>
            </div>
          </div>

          {/* Real Satellite Map Area */}
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #E2E8F0',
              overflow: 'hidden',
              position: 'relative',
              minHeight: '580px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Top-left property name badge matching Figma */}
            <div
              style={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                zIndex: 10,
                backgroundColor: '#FFFFFF',
                borderRadius: '6px',
                padding: '6px 14px',
                fontSize: '13px',
                fontWeight: 700,
                color: '#1E293B',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              }}
            >
              {propertyName}
            </div>

            {/* Real Azure Maps Satellite Container */}
            <div
              id="liveParkingMap"
              ref={mapRef}
              style={{
                flex: 1,
                width: '100%',
                minHeight: '580px',
                position: 'relative',
                borderRadius: '16px',
              }}
            />
          </div>
        </div>

        {/* RIGHT SECTION: Space Details Panel matching Figma card */}
        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
            padding: '28px 24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: activeSpace ? 'flex-start' : 'center',
            alignItems: activeSpace ? 'stretch' : 'center',
            minHeight: '560px',
          }}
        >
          {!activeSpace ? (
            /* Empty State (Figma Match) */
            <div
              style={{
                textAlign: 'center',
                padding: '40px 16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <PlaceOutlinedIcon sx={{ fontSize: 72, color: '#9CA3AF', marginBottom: '20px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1E293B', margin: '0 0 10px 0' }}>
                No Space Selected
              </h3>
              <p style={{ fontSize: '13px', color: '#64748B', margin: 0, lineHeight: 1.6, maxWidth: '240px' }}>
                Click on an occupied or issue marker on the map to view session details
              </p>
            </div>
          ) : activeSpace.status === 'Available' ? (
            /* Available Space State */
            <div
              style={{
                textAlign: 'center',
                padding: '40px 16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#22C55E', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1E293B', margin: '0 0 8px 0' }}>
                {activeSpace.pslName}
              </h3>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 12px 0' }}>
                This space is currently available for booking.
              </p>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A' }}>
                Rate: {activeSpace.rate}
              </div>
            </div>
          ) : (
            /* Active Session State matching Screenshot */
            <div>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                <img
                  src={activeSessionCarIconSvg}
                  alt="Active Session"
                  style={{ width: '22px', height: '14px', objectFit: 'contain' }}
                />
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', margin: 0 }}>
                  Active Session
                </h3>
              </div>
              <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '14px' }}>
                Space: {activeSpace.pslName}
              </div>

              <div style={{ height: '1px', backgroundColor: '#E2E8F0', marginBottom: '18px' }} />

              {/* Rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
                {/* Parking Duration */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13.5px' }}>
                  <span style={{ color: '#64748B' }}>Parking Duration</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: '#0F172A', fontSize: '14px' }}>
                    <img
                      src={clockIconSvg}
                      alt="Clock"
                      style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                    />
                    <span>{formatDurationText(activeSpace.vehicleInfo?.parkingDuration || activeSpace.vehicleInfo?.durationText)}</span>
                  </div>
                </div>

                {/* Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13.5px' }}>
                  <span style={{ color: '#64748B' }}>Status</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: '#0F172A', fontSize: '14px' }}>
                    <span
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: activeSpace.status === 'Issue' ? '#EF4444' : '#EAB308',
                        display: 'inline-block',
                      }}
                    />
                    <span>{activeSpace.status === 'Issue' ? 'Issue Reported' : (activeSpace.status || 'Occupied')}</span>
                  </div>
                </div>

                {/* Property */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13.5px' }}>
                  <span style={{ color: '#64748B' }}>Property</span>
                  <span style={{ fontWeight: 600, color: '#0F172A', fontSize: '14px', textAlign: 'right', maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {displayProperty}
                  </span>
                </div>

                {/* Start Time */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13.5px' }}>
                  <span style={{ color: '#64748B' }}>Start Time</span>
                  <span style={{ fontWeight: 600, color: '#0F172A', fontSize: '14px' }}>
                    {displayStartTime}
                  </span>
                </div>
              </div>

              {/* Current Fee Accrued Banner */}
              <div
                style={{
                  backgroundColor: '#E0F2FE',
                  borderRadius: '10px',
                  padding: '16px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                }}
              >
                <span style={{ fontSize: '13.5px', color: '#475569', fontWeight: 500 }}>
                  Current Fee Accrued
                </span>
                <span style={{ fontSize: '22px', fontWeight: 700, color: '#1D4ED8' }}>
                  {activeSpace.vehicleInfo?.currentFee || '$0.00'}
                </span>
              </div>

              {/* Session Duration Progress Track */}
              <div style={{ marginBottom: '22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '11.5px', color: '#64748B', fontWeight: 500 }}>0h</span>
                  <span style={{ fontSize: '11.5px', color: '#64748B', fontWeight: 500 }}>Session Duration</span>
                  <span style={{ fontSize: '11.5px', color: '#64748B', fontWeight: 500 }}>{sessionHoursDisplay}h</span>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '6px',
                    backgroundColor: '#E2E8F0',
                    borderRadius: '9999px',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      width: `${sessionProgressPercent}%`,
                      height: '100%',
                      backgroundColor: '#1D4ED8',
                      borderRadius: '9999px',
                      transition: 'width 0.4s ease',
                    }}
                  />
                </div>
              </div>

              {/* Reported Issue Note if present */}
              {activeSpace.vehicleInfo?.issueType && (
                <div
                  style={{
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FCA5A5',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    marginBottom: '18px',
                  }}
                >
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#991B1B' }}>
                    Reported Note:
                  </div>
                  <div style={{ fontSize: '13px', color: '#7F1D1D', marginTop: '2px' }}>
                    {activeSpace.vehicleInfo.issueType}
                  </div>
                </div>
              )}

              <div style={{ height: '1px', backgroundColor: '#F1F5F9', marginBottom: '20px' }} />

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  onClick={() => {
                    setSelectedEjectReason('Overstayed Session');
                    setIsEjectModalOpen(true);
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: '#C51E28',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'background-color 0.2s',
                    boxShadow: '0 1px 3px rgba(197, 30, 40, 0.2)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#A71822')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#C51E28')}
                >
                  <img
                    src={ejectIconSvg}
                    alt="Eject"
                    style={{ width: '18px', height: '16px', objectFit: 'contain' }}
                  />
                  <span>Eject Robotaxi</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedIssueType('');
                    setIssueNotes('');
                    setIsReportIssueOpen(true);
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: '#FFFFFF',
                    color: '#1E293B',
                    border: '1px solid #CBD5E1',
                    borderRadius: '6px',
                    padding: '11px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F8FAFC')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#FFFFFF')}
                >
                  <img
                    src={reportIssueIconSvg}
                    alt="Report Issue"
                    style={{ width: '18px', height: '18px', objectFit: 'contain' }}
                  />
                  <span>Report Issue</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MODAL 1: Single Robotaxi Eject */}
      <Dialog
        open={isEjectModalOpen}
        onClose={() => !isSubmittingEjection && setIsEjectModalOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '490px',
            maxWidth: '92vw',
            borderRadius: '16px',
            padding: '24px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'visible',
            m: '16px',
            boxSizing: 'border-box',
          },
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
          <div>
            <div style={{ fontWeight: 700, color: '#111827', fontSize: '18px', marginBottom: '3px' }}>
              Eject Robotaxi
            </div>
            <div style={{ color: '#6B7280', fontSize: '13px' }}>
              Space: {activeSpace?.pslName || 'PSL1'} &nbsp;|&nbsp; Please select a reason and timing for ejection
            </div>
          </div>
          <IconButton onClick={() => setIsEjectModalOpen(false)} size="small" sx={{ color: '#9CA3AF', p: 0.5, mt: -0.5, mr: -0.5, '&:hover': { color: '#4B5563' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Issue Type */}
        <div style={{ fontWeight: 600, color: '#111827', fontSize: '13px', marginBottom: '8px' }}>
          Issue Type
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
          {ejectReasons.map((reason) => {
            const isSelected = selectedEjectReason === reason.label;
            return (
              <div
                key={reason.label}
                onClick={() => setSelectedEjectReason(reason.label)}
                style={{
                  padding: '11px 8px',
                  borderRadius: '8px',
                  border: isSelected ? '1.5px solid #4B88FF' : '1px solid #E5E7EB',
                  backgroundColor: isSelected ? '#EDF4FF' : '#FFFFFF',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  textAlign: 'center',
                  transition: 'all 0.15s ease',
                  userSelect: 'none',
                }}
              >
                <div style={{ color: '#111827', display: 'flex', alignItems: 'center' }}>
                  {reason.icon}
                </div>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: isSelected ? 600 : 500,
                    color: '#111827',
                    lineHeight: 1.25,
                  }}
                >
                  {reason.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Eject Timing */}
        <div style={{ fontWeight: 600, color: '#111827', fontSize: '13px', marginBottom: '8px' }}>
          Eject Timing
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
          {ejectTimings.map((t) => {
            const isSelected = selectedEjectTiming === t.title;
            return (
              <div
                key={t.title}
                onClick={() => setSelectedEjectTiming(t.title)}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: isSelected ? '1.5px solid #4B88FF' : '1px solid #E5E7EB',
                  backgroundColor: isSelected ? '#EDF4FF' : '#FFFFFF',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  transition: 'all 0.15s ease',
                  userSelect: 'none',
                }}
              >
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                  {t.title}
                </span>
                <span style={{ fontSize: '11.5px', color: '#6B7280' }}>
                  {t.subtitle}
                </span>
              </div>
            );
          })}
        </div>

        {/* Additional Notes */}
        <div style={{ fontWeight: 600, color: '#111827', fontSize: '13px', marginBottom: '8px' }}>
          Additional Notes (optional)
        </div>
        <textarea
          rows={3}
          placeholder="Describe the issue in detail.."
          value={ejectNotes}
          onChange={(e) => setEjectNotes(e.target.value)}
          style={{
            width: '100%',
            height: '72px',
            minHeight: '72px',
            maxHeight: '72px',
            backgroundColor: '#F3F4F6',
            border: 'none',
            outline: 'none',
            borderRadius: '8px',
            padding: '10px 14px',
            fontSize: '13px',
            fontFamily: 'inherit',
            color: '#1F2937',
            boxSizing: 'border-box',
            resize: 'none',
            display: 'block',
            marginBottom: '18px',
          }}
        />

        {/* Actions Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
          <button
            type="button"
            onClick={() => setIsEjectModalOpen(false)}
            disabled={isSubmittingEjection}
            style={{
              backgroundColor: '#F3F4F6',
              color: '#111827',
              border: 'none',
              borderRadius: '6px',
              padding: '9px 24px',
              fontSize: '13.5px',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: 'none',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#E5E7EB')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#F3F4F6')}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmEject}
            disabled={isSubmittingEjection}
            style={{
              backgroundColor: '#C51E28',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              padding: '9px 24px',
              fontSize: '13.5px',
              fontWeight: 600,
              cursor: isSubmittingEjection ? 'not-allowed' : 'pointer',
              boxShadow: 'none',
              opacity: isSubmittingEjection ? 0.7 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#A71822')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#C51E28')}
          >
            {isSubmittingEjection ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : 'Confirm Ejection'}
          </button>
        </div>
      </Dialog>

      {/* MODAL 2: Eject ALL Active Robotaxis */}
      <Dialog
        open={isEjectAllModalOpen}
        onClose={() => !isSubmittingEjection && setIsEjectAllModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '20px 24px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <DialogTitle sx={{ p: '0 0 16px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '18px', mb: '4px' }}>
                Eject All Active Robotaxis
              </Typography>
              <Typography sx={{ color: '#6B7280', fontSize: '13px' }}>
                Initiate ejection for all active robotaxis in {propertyName}
              </Typography>
            </div>
            <IconButton onClick={() => setIsEjectAllModalOpen(false)} size="small" sx={{ color: '#9CA3AF', p: 0 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent sx={{ p: '0', overflowY: 'visible' }}>
          <Alert severity="warning" sx={{ mb: '16px', borderRadius: '8px', fontSize: '13px', backgroundColor: '#FEF3C7', color: '#92400E' }}>
            This action will initiate ejection requests for <strong>all {occupiedCount} active robotaxis</strong> currently parked in <strong>{propertyName}</strong>.
          </Alert>

          <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '13px', mb: '10px' }}>
            Issue Type
          </Typography>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
            {ejectReasons.map((reason) => {
              const isSelected = selectedEjectReason === reason.label;
              return (
                <div
                  key={reason.label}
                  onClick={() => setSelectedEjectReason(reason.label)}
                  style={{
                    padding: '16px 12px',
                    borderRadius: '8px',
                    border: isSelected ? '1.5px solid #4B88FF' : '1px solid #E5E7EB',
                    backgroundColor: isSelected ? '#EDF4FF' : '#FFFFFF',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    textAlign: 'center',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ color: '#111827' }}>
                    {reason.icon}
                  </div>
                  <span style={{ fontSize: '12.5px', fontWeight: isSelected ? 600 : 500, color: '#111827' }}>
                    {reason.label}
                  </span>
                </div>
              );
            })}
          </div>

          <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '13px', mb: '10px' }}>
            Eject Timing
          </Typography>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {ejectTimings.map((t) => {
              const isSelected = selectedEjectTiming === t.title;
              return (
                <div
                  key={t.title}
                  onClick={() => setSelectedEjectTiming(t.title)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: isSelected ? '1.5px solid #4B88FF' : '1px solid #E5E7EB',
                    backgroundColor: isSelected ? '#EDF4FF' : '#FFFFFF',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3px',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#111827' }}>
                    {t.title}
                  </span>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>
                    {t.subtitle}
                  </span>
                </div>
              );
            })}
          </div>
        </DialogContent>

        <DialogActions sx={{ p: '20px 0 0 0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Button
            onClick={() => setIsEjectAllModalOpen(false)}
            disabled={isSubmittingEjection}
            sx={{
              backgroundColor: '#F3F4F6',
              color: '#374151',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13.5px',
              px: 3,
              py: '8px',
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#E5E7EB', boxShadow: 'none' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmEjectAll}
            disabled={isSubmittingEjection}
            variant="contained"
            sx={{
              backgroundColor: '#C51E28',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13.5px',
              px: 3,
              py: '8px',
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#A71822', boxShadow: 'none' },
            }}
          >
            {isSubmittingEjection ? <CircularProgress size={18} color="inherit" /> : 'Eject All Active Vehicles'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL 3: Report Issue */}
      <Dialog
        open={isReportIssueOpen}
        onClose={() => !isSubmittingIssue && setIsReportIssueOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '490px',
            maxWidth: '92vw',
            borderRadius: '16px',
            padding: '24px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'visible',
            m: '16px',
            boxSizing: 'border-box',
          },
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
          <div>
            <div style={{ fontWeight: 700, color: '#111827', fontSize: '18px', marginBottom: '3px' }}>
              Report Issue
            </div>
            <div style={{ color: '#6B7280', fontSize: '13px' }}>
              Space: {activeSpace?.pslName || 'PSL1'} &nbsp;|&nbsp; Select the type of issue you want to report
            </div>
          </div>
          <IconButton onClick={() => setIsReportIssueOpen(false)} size="small" sx={{ color: '#9CA3AF', p: 0.5, mt: -0.5, mr: -0.5, '&:hover': { color: '#4B5563' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Issue Category */}
        <div style={{ fontWeight: 600, color: '#111827', fontSize: '13px', marginBottom: '8px' }}>
          Issue Category
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
          {issueTypes.map((it) => {
            const isSelected = selectedIssueType === it.label;
            return (
              <div
                key={it.label}
                onClick={() => setSelectedIssueType(it.label)}
                style={{
                  padding: '11px 8px',
                  borderRadius: '8px',
                  border: isSelected ? '1.5px solid #4B88FF' : '1px solid #E5E7EB',
                  backgroundColor: isSelected ? '#EDF4FF' : '#FFFFFF',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  textAlign: 'center',
                  transition: 'all 0.15s ease',
                  userSelect: 'none',
                }}
              >
                <div style={{ color: '#111827', display: 'flex', alignItems: 'center' }}>
                  {it.icon}
                </div>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: isSelected ? 600 : 500,
                    color: '#111827',
                    lineHeight: 1.25,
                  }}
                >
                  {it.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Additional Notes */}
        <div style={{ fontWeight: 600, color: '#111827', fontSize: '13px', marginBottom: '6px' }}>
          Additional Notes (optional)
        </div>
        <textarea
          rows={2}
          placeholder="Describe the issue in detail.."
          value={issueNotes}
          onChange={(e) => setIssueNotes(e.target.value)}
          style={{
            width: '100%',
            height: '56px',
            minHeight: '56px',
            maxHeight: '56px',
            backgroundColor: '#F3F4F6',
            border: 'none',
            outline: 'none',
            borderRadius: '8px',
            padding: '8px 12px',
            fontSize: '13px',
            fontFamily: 'inherit',
            color: '#1F2937',
            boxSizing: 'border-box',
            resize: 'none',
            display: 'block',
            marginBottom: '16px',
          }}
        />

        {/* Actions Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
          <button
            type="button"
            onClick={() => setIsReportIssueOpen(false)}
            disabled={isSubmittingIssue}
            style={{
              backgroundColor: '#F3F4F6',
              color: '#111827',
              border: 'none',
              borderRadius: '6px',
              padding: '9px 24px',
              fontSize: '13.5px',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: 'none',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#E5E7EB')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#F3F4F6')}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmitIssue}
            disabled={!selectedIssueType || isSubmittingIssue}
            style={{
              backgroundColor: !selectedIssueType ? '#94A3B8' : '#C51E28',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              padding: '9px 24px',
              fontSize: '13.5px',
              fontWeight: 600,
              cursor: !selectedIssueType || isSubmittingIssue ? 'not-allowed' : 'pointer',
              boxShadow: 'none',
              opacity: !selectedIssueType || isSubmittingIssue ? 0.6 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (selectedIssueType && !isSubmittingIssue) {
                e.currentTarget.style.backgroundColor = '#A71822';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedIssueType && !isSubmittingIssue) {
                e.currentTarget.style.backgroundColor = '#C51E28';
              }
            }}
          >
            {isSubmittingIssue ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : 'Submit Issue Report'}
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default LiveParkingSpaceManagement;
